import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiSearch, FiFile, FiDownload, FiEye, FiFileText, FiImage, FiFilter, FiFolder, FiUserPlus, FiLoader, FiMail, FiPaperclip, FiExternalLink } from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileZip } from 'react-icons/ai';
import { BsFiletypeCsv, BsRobot } from 'react-icons/bs';
import { searchAPI, documentsAPI, usersAPI, actionsAPI, sharepointAPI, exchangeAPI, foldersAPI } from '../lib/api';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import CsvExtractModal from '../components/CsvExtractModal';
import EmailDetailModal from '../components/EmailDetailModal';
import { useToast } from '../components/ToastContainer';

const Search = () => {
  const [query, setQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('relevance'); // relevance, date_desc, date_asc, name_asc, name_desc, size_desc, size_asc
  const [filterType, setFilterType] = useState('all'); // all, pdf, word, excel, image, other
  const [filterAiProcessed, setFilterAiProcessed] = useState('all'); // all, processed, unprocessed
  const [sharepointLimit, setSharepointLimit] = useState(100);
  const maxSharepointLimit = 1000;
  const [exchangeLimit, setExchangeLimit] = useState(100);
  const maxExchangeLimit = 1000;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const isAdminOrManager = profile?.role === 'admin' || profile?.role === 'manager';
  const { showSuccess, showError, showInfo } = useToast();

  // CSV extract / assign action state
  const [csvExtractDoc, setCsvExtractDoc] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignDoc, setAssignDoc] = useState(null);
  const [assignForm, setAssignForm] = useState({ assigned_to: '', action_type: 'approval', priority: 'normal', due_date: '', notes: '' });

  // SharePoint download choice modal state
  const [showSharePointDownloadModal, setShowSharePointDownloadModal] = useState(false);
  const [sharePointDocForDownload, setSharePointDocForDownload] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState('');

  // Exchange email detail modal state
  const [emailDetailDoc, setEmailDetailDoc] = useState(null);

  // Handle URL query parameters (for tag clicks from dashboard)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlQuery = params.get('q');
    if (urlQuery) {
      setQuery(urlQuery);
      setSearchQuery(urlQuery);
    }
  }, [location.search]);

  const getFileIcon = (filename, mimeType) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    
    if (mimeType?.includes('pdf') || ext === 'pdf') {
      return <AiOutlineFilePdf className="w-6 h-6 text-red-500" />;
    }
    if (mimeType?.includes('word') || ['doc', 'docx'].includes(ext)) {
      return <AiOutlineFileWord className="w-6 h-6 text-blue-500" />;
    }
    if (mimeType?.includes('sheet') || mimeType?.includes('excel') || ['xls', 'xlsx'].includes(ext)) {
      return <AiOutlineFileExcel className="w-6 h-6 text-green-600" />;
    }
    if (ext === 'csv') {
      return <BsFiletypeCsv className="w-6 h-6 text-green-600" />;
    }
    if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
      return <FiImage className="w-6 h-6 text-purple-500" />;
    }
    if (mimeType?.includes('zip') || mimeType?.includes('compressed') || ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return <AiOutlineFileZip className="w-6 h-6 text-yellow-600" />;
    }
    if (mimeType?.includes('text') || ['txt', 'log', 'md'].includes(ext)) {
      return <FiFileText className="w-6 h-6 text-gray-500" />;
    }
    
    return <FiFile className="w-6 h-6 text-gray-400" />;
  };

  const { data, isLoading } = useQuery({
    queryKey: ['search', searchQuery, sharepointLimit, exchangeLimit],
    queryFn: async () => {
      // Fetch regular, SharePoint, and Exchange search results in parallel
      const [regularResults, sharepointResults, exchangeResults] = await Promise.allSettled([
        searchAPI.search({ q: searchQuery }),
        sharepointAPI.search({ query: searchQuery, limit: sharepointLimit }),
        exchangeAPI.search({ query: searchQuery, limit: 10, maxResults: exchangeLimit })
      ]);

      return {
        data: {
          results: regularResults.status === 'fulfilled' ? regularResults.value.data.results : [],
          total: regularResults.status === 'fulfilled' ? regularResults.value.data.total : 0,
          sharepointTotalResults: sharepointResults.status === 'fulfilled' ? (sharepointResults.value.data.totalResults || 0) : 0,
          sharepointMoreAvailable: sharepointResults.status === 'fulfilled' ? !!sharepointResults.value.data.moreResultsAvailable : false,
          searchMethod: sharepointResults.status === 'fulfilled' ? (sharepointResults.value.data.searchMethod || 'unknown') : 'unknown',
          sharepointResults: sharepointResults.status === 'fulfilled' ? sharepointResults.value.data.results.map(r => ({
            id: r.id,
            source: 'sharepoint',
            name: r.name,
            created_at: r.createdDateTime || r.lastModifiedDateTime,
            mime_type: r.mimeType,
            file_size: r.size,
            web_url: r.webUrl,
            webUrl: r.webUrl,
            // These properties are needed for the process action
            connectionId: r.connectionId,
            tenantName: r.tenantName,
            driveId: r.driveId,
            siteId: r.siteId,
            parentReference: r.parentReference,
            // Additional metadata
            summary: r.summary,
            isFolder: r.isFolder,
            rank: r.rank
          })) : [],
          exchangeResults: exchangeResults.status === 'fulfilled' ? (exchangeResults.value.data.results || []).map(msg => ({
            id: `exchange-${msg.mailboxId}-${msg.id}`,
            source: 'exchange',
            name: msg.subject || 'No Subject',
            created_at: msg.receivedDateTime,
            from: msg.from,
            toRecipients: msg.toRecipients,
            ccRecipients: msg.ccRecipients,
            bodyPreview: msg.bodyPreview,
            hasAttachments: msg.hasAttachments,
            attachmentNames: msg.attachmentNames || [],
            webLink: msg.webLink,
            importance: msg.importance,
            mailboxId: msg.mailboxId,
            mailboxEmail: msg.mailboxEmail,
            messageId: msg.id,
          })) : [],
          exchangeEstimatedTotalResults: exchangeResults.status === 'fulfilled'
            ? (exchangeResults.value.data.estimatedTotalResults || 0)
            : 0,
          exchangeMoreAvailable: exchangeResults.status === 'fulfilled'
            ? !!exchangeResults.value.data.moreResultsAvailable
            : false,
        }
      };
    },
    enabled: searchQuery.length > 0,
  });

  useEffect(() => {
    setSharepointLimit(100);
    setExchangeLimit(100);
  }, [searchQuery]);

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersAPI.getAll(),
    enabled: true,
  });

  const { data: foldersData } = useQuery({
    queryKey: ['folders'],
    queryFn: () => foldersAPI.getAll(),
    enabled: true,
  });

  const assignActionMutation = useMutation({
    mutationFn: (data) => actionsAPI.create(data),
    onMutate: () => {
      showInfo('Sending email notification...', 10000);
    },
    onSuccess: () => {
      setShowAssignModal(false);
      setAssignDoc(null);
      setAssignForm({ assigned_to: '', action_type: 'approval', priority: 'normal', due_date: '', notes: '' });
      showSuccess('Action assigned and email notification sent!');
      queryClient.invalidateQueries(['search']);
    },
    onError: (error) => {
      showError(`Failed to assign action: ${error.response?.data?.message || error.message}`);
    },
  });

  // Filter and sort results
  const filteredAndSortedResults = useMemo(() => {
    if (!data?.data?.results) return [];

    // Merge regular documents, SharePoint documents, and Exchange emails
    let results = [
      ...data.data.results,
      ...(data.data.sharepointResults || []),
      ...(data.data.exchangeResults || [])
    ];
    
    // Apply file type filter
    if (filterType !== 'all') {
      results = results.filter(doc => {
        const ext = doc.name?.split('.').pop()?.toLowerCase();
        const mime = doc.mime_type?.toLowerCase();
        
        switch (filterType) {
          case 'pdf':
            return mime?.includes('pdf') || ext === 'pdf';
          case 'word':
            return mime?.includes('word') || ['doc', 'docx'].includes(ext);
          case 'excel':
            return mime?.includes('sheet') || mime?.includes('excel') || ['xls', 'xlsx', 'csv'].includes(ext);
          case 'image':
            return mime?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext);
          case 'exchange':
            return doc.source === 'exchange';
          case 'email':
            return ['eml', 'msg'].includes(ext) || mime === 'message/rfc822' || mime === 'application/vnd.ms-outlook';
          case 'other':
            return !['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp', 'eml', 'msg'].includes(ext);
          default:
            return true;
        }
      });
    }
    
    // Apply AI processed filter
    if (filterAiProcessed !== 'all') {
      results = results.filter(doc => {
        const hasAiData = doc.document_ai_metadata && doc.document_ai_metadata.length > 0;
        return filterAiProcessed === 'processed' ? hasAiData : !hasAiData;
      });
    }
    
    // Apply sorting
    results.sort((a, b) => {
      switch (sortBy) {
        case 'date_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'date_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'name_asc':
          return a.name.localeCompare(b.name);
        case 'name_desc':
          return b.name.localeCompare(a.name);
        case 'size_desc':
          return (b.file_size || 0) - (a.file_size || 0);
        case 'size_asc':
          return (a.file_size || 0) - (b.file_size || 0);
        case 'relevance':
        default:
          return 0; // Keep original order (search relevance)
      }
    });
    
    return results;
  }, [data, filterType, filterAiProcessed, sortBy]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(query);
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Search</h1>
        <p className="text-gray-600 mt-2">Search across all your documents</p>
      </div>

      {/* Search Form */}
      <div className="card mb-6">
        <form onSubmit={handleSearch} className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search documents, SharePoint files, and Exchange emails..."
              className="input pl-10"
            />
          </div>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="card">
          <p className="text-gray-600">Searching...</p>
        </div>
      ) : searchQuery && data ? (
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">
              Search Results ({filteredAndSortedResults.length})
              {data?.data?.sharepointTotalResults > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (SharePoint: showing {data.data.sharepointResults?.length || 0} of {data.data.sharepointTotalResults})
                </span>
              )}
              {data?.data?.exchangeResults?.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  (Exchange: showing {data.data.exchangeResults.length} of {data.data.exchangeEstimatedTotalResults || data.data.exchangeResults.length}{data.data.exchangeMoreAvailable ? '+' : ''})
                </span>
              )}
            </h2>
          </div>

          {/* Filters and Sorting */}
          <div className="flex flex-wrap items-center gap-4 mb-6 pb-4 border-b border-gray-200">
            {/* Sort By */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Sort by:</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="relevance">Relevance</option>
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="size_desc">Largest First</option>
                <option value="size_asc">Smallest First</option>
              </select>
            </div>

            {/* File Type Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">File type:</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Types</option>
                <option value="pdf">PDF</option>
                <option value="word">Word</option>
                <option value="excel">Excel/CSV</option>
                <option value="image">Images</option>
                <option value="exchange">Exchange Email</option>
                <option value="email">Email (EML/MSG)</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* AI Processed Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">AI Status:</label>
              <select
                value={filterAiProcessed}
                onChange={(e) => setFilterAiProcessed(e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">All Documents</option>
                <option value="processed">AI Processed</option>
                <option value="unprocessed">Not Processed</option>
              </select>
            </div>

            {/* Active Filters Indicator */}
            {(filterType !== 'all' || filterAiProcessed !== 'all' || sortBy !== 'relevance') && (
              <button
                onClick={() => {
                  setFilterType('all');
                  setFilterAiProcessed('all');
                  setSortBy('relevance');
                }}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium ml-auto"
              >
                Clear Filters
              </button>
            )}
          </div>

          <div className="space-y-4">
            {filteredAndSortedResults.length > 0 ? (
              filteredAndSortedResults.map((doc) => (
              <div
                key={doc.id}
                className="p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
              >
                <div className="flex items-start gap-4">
                  {/* File Type Icon */}
                  <div className="mt-1">
                    {doc.source === 'exchange' ? (
                      <FiMail className="w-6 h-6 text-purple-500" />
                    ) : (
                      getFileIcon(doc.name, doc.mime_type)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3
                        className="font-medium text-gray-900 cursor-pointer hover:text-primary-600"
                        onClick={() => {
                          if (doc.source === 'exchange') {
                            setEmailDetailDoc(doc);
                          } else if (doc.source === 'sharepoint') {
                            if (doc.web_url) {
                              window.open(doc.web_url, '_blank');
                            }
                          } else {
                            navigate(`/documents/${doc.id}`);
                          }
                        }}
                      >
                        {doc.name}
                      </h3>
                      {/* Exchange Email Badge */}
                      {doc.source === 'exchange' && (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700 border border-purple-200"
                          title="Exchange Email"
                        >
                          <FiMail className="w-3 h-3" />
                          Exchange Email
                        </span>
                      )}
                      {/* SharePoint Badge */}
                      {doc.source === 'sharepoint' && (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 border border-blue-200"
                          title="SharePoint Document"
                        >
                          SharePoint
                        </span>
                      )}
                      {/* AI Processed Icon */}
                      {doc.document_ai_metadata && doc.document_ai_metadata.length > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-purple-100 text-purple-700"
                          title="AI Processed"
                        >
                          <BsRobot className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                    {/* Exchange email metadata */}
                    {doc.source === 'exchange' && (
                      <div className="mt-1 space-y-1">
                        {doc.from && (
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">From:</span> {doc.from.name} &lt;{doc.from.email}&gt;
                          </p>
                        )}
                        {doc.toRecipients && doc.toRecipients.length > 0 && (
                          <p className="text-sm text-gray-500 truncate">
                            <span className="font-medium">To:</span> {doc.toRecipients.map(r => r.name || r.email).join(', ')}
                          </p>
                        )}
                        {doc.bodyPreview && (
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                            {doc.bodyPreview}
                          </p>
                        )}
                      </div>
                    )}
                    {/* Summary - Regular documents */}
                    {doc.source !== 'sharepoint' && doc.source !== 'exchange' && doc.document_content?.[0]?.summary && (
                      <p className="text-sm text-gray-600 mt-2">
                        {doc.document_content[0].summary}
                      </p>
                    )}
                    {/* Summary - SharePoint documents */}
                    {doc.source === 'sharepoint' && doc.summary && (
                      <p className="text-sm text-gray-600 mt-2">
                        {doc.summary}
                      </p>
                    )}
                    <div className="flex items-center flex-wrap gap-2 mt-3">
                      <span className="text-xs text-gray-500">
                        {new Date(doc.created_at).toLocaleDateString()}
                      </span>

                      {/* Exchange: Mailbox */}
                      {doc.source === 'exchange' && doc.mailboxEmail && (
                        <span className="text-xs px-2 py-1 bg-purple-50 text-purple-700 rounded flex items-center gap-1">
                          <FiMail className="w-3 h-3" />
                          {doc.mailboxEmail}
                        </span>
                      )}

                      {/* Exchange: Importance */}
                      {doc.source === 'exchange' && doc.importance && doc.importance !== 'normal' && (
                        <span className={`text-xs px-2 py-1 rounded ${doc.importance === 'high' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {doc.importance === 'high' ? 'High Priority' : doc.importance}
                        </span>
                      )}

                      {/* Exchange: Attachments */}
                      {doc.source === 'exchange' && doc.hasAttachments && doc.attachmentNames?.length > 0 && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex items-center gap-1">
                          <FiPaperclip className="w-3 h-3" />
                          {doc.attachmentNames.length} attachment{doc.attachmentNames.length > 1 ? 's' : ''}
                        </span>
                      )}

                      {/* Folder */}
                      {doc.folder?.name && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded flex items-center gap-1">
                          <FiFolder className="w-3 h-3" />
                          {doc.folder.name}
                        </span>
                      )}

                      {/* Document Type */}
                      {doc.document_ai_metadata?.[0]?.document_type && (
                        <span className="text-xs px-2 py-1 bg-primary-100 text-primary-700 rounded">
                          {doc.document_ai_metadata[0].document_type}
                        </span>
                      )}

                      {/* User-defined Tags */}
                      {doc.tags && doc.tags.length > 0 && (
                        <>
                          {doc.tags.slice(0, 3).map((tag, idx) => (
                            <span key={`tag-${idx}`} className="text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200">
                              {tag}
                            </span>
                          ))}
                          {doc.tags.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{doc.tags.length - 3} more
                            </span>
                          )}
                        </>
                      )}

                      {/* AI Suggested Tags */}
                      {doc.document_ai_metadata?.[0]?.suggested_tags && doc.document_ai_metadata[0].suggested_tags.length > 0 && (
                        <>
                          {doc.document_ai_metadata[0].suggested_tags.slice(0, 2).map((tag, idx) => (
                            <span key={`ai-${idx}`} className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-700 border border-purple-200">
                              {tag}
                            </span>
                          ))}
                        </>
                      )}
                    </div>

                    {/* Exchange: Expandable attachments list */}
                    {doc.source === 'exchange' && doc.hasAttachments && doc.attachmentNames?.length > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span className="font-medium">Attachments:</span>{' '}
                        {doc.attachmentNames.join(', ')}
                      </div>
                    )}
                  </div>
                  {/* Action Buttons */}
                  <div className="flex items-center gap-1">
                    {/* View Details - Exchange emails */}
                    {doc.source === 'exchange' && (
                      <button
                        onClick={() => setEmailDetailDoc(doc)}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="View Details"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                    )}
                    {/* Download .eml - Exchange emails */}
                    {doc.source === 'exchange' && (
                      <button
                        onClick={async () => {
                          try {
                            const response = await exchangeAPI.downloadMessage(doc.mailboxId, doc.messageId);
                            const blob = response.data;
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            const filename = (doc.name || 'email').replace(/[<>:"/\\|?*]/g, '_').substring(0, 100) + '.eml';
                            link.setAttribute('download', filename);
                            document.body.appendChild(link);
                            link.click();
                            setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
                          } catch (error) {
                            console.error('Download error:', error);
                            showError('Failed to download email');
                          }
                        }}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                        title="Download .eml"
                      >
                        <FiDownload className="w-4 h-4" />
                      </button>
                    )}
                    {/* Open in Outlook - Exchange emails */}
                    {doc.source === 'exchange' && doc.webLink && (
                      <button
                        onClick={() => window.open(doc.webLink, '_blank')}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Open in Outlook"
                      >
                        <FiExternalLink className="w-4 h-4" />
                      </button>
                    )}
                    {/* View Details - Only for regular documents */}
                    {doc.source !== 'sharepoint' && doc.source !== 'exchange' && (
                      <button
                        onClick={() => navigate(`/documents/${doc.id}`)}
                        className="p-1.5 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="View Details"
                      >
                        <FiEye className="w-4 h-4" />
                      </button>
                    )}
                    {doc.source !== 'exchange' && (
                      <button
                        onClick={async () => {
                          // Handle SharePoint documents - show choice modal
                          if (doc.source === 'sharepoint') {
                            setSharePointDocForDownload(doc);
                            setShowSharePointDownloadModal(true);
                            return;
                          }

                          // Regular document download
                          try {
                            const response = await documentsAPI.download(doc.id);
                            const blob = response.data;
                            const url = window.URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', doc.name);
                            document.body.appendChild(link);
                            link.click();
                            setTimeout(() => { link.remove(); window.URL.revokeObjectURL(url); }, 100);
                          } catch (error) {
                            console.error('Download error:', error);
                            showError('Failed to download document');
                          }
                        }}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors"
                        title={doc.source === 'sharepoint' ? 'Download Options' : 'Download'}
                      >
                        <FiDownload className="w-4 h-4" />
                      </button>
                    )}
                    {/* CSV Extract Button - Only for regular documents */}
                    {doc.source !== 'sharepoint' && doc.source !== 'exchange' && (
                      <button
                        onClick={() => setCsvExtractDoc(doc)}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                        title="CSV Extract - AI powered data extraction"
                      >
                        <BsFiletypeCsv className="w-4 h-4" />
                      </button>
                    )}
                    {/* Assign Action Button - For regular and SharePoint documents only */}
                    {doc.source !== 'exchange' && (
                      <button
                        onClick={() => {
                          setAssignDoc(doc);
                          setShowAssignModal(true);
                          setAssignForm({ assigned_to: '', action_type: 'approval', priority: 'normal', due_date: '', notes: '' });
                        }}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Assign Action"
                      >
                        <FiUserPlus className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
            ) : (
              <p className="text-gray-500 text-center py-8">
                No results match your filters. Try adjusting the filters above.
              </p>
            )}
          </div>

          {/* Load More Controls */}
          {data?.data?.sharepointTotalResults > (data?.data?.sharepointResults?.length || 0) && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                SharePoint results are paged. Load more to fetch additional SharePoint matches.
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSharepointLimit(prev => Math.min(prev + 100, maxSharepointLimit))}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                >
                  Load more SharePoint results
                </button>
                <button
                  onClick={() => setSharepointLimit(Math.min(data.data.sharepointTotalResults, maxSharepointLimit))}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                >
                  Load all SharePoint (up to {maxSharepointLimit})
                </button>
              </div>
            </div>
          )}
          {data?.data?.exchangeMoreAvailable && exchangeLimit < maxExchangeLimit && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                Exchange results are capped. Load more to fetch additional Exchange matches.
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExchangeLimit(prev => Math.min(prev + 100, maxExchangeLimit))}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                >
                  Load more Exchange results
                </button>
                <button
                  onClick={() => setExchangeLimit(maxExchangeLimit)}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
                >
                  Load all Exchange (up to {maxExchangeLimit})
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="text-center py-12">
            <FiSearch className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Enter a search query to find documents</p>
        </div>
      </div>
      )}

      {/* CSV Extract Modal */}
      {csvExtractDoc && (
        <CsvExtractModal
          document={csvExtractDoc}
          onClose={() => setCsvExtractDoc(null)}
        />
      )}

      {/* Email Detail Modal */}
      {emailDetailDoc && (
        <EmailDetailModal
          mailboxId={emailDetailDoc.mailboxId}
          messageId={emailDetailDoc.messageId}
          subject={emailDetailDoc.name}
          onClose={() => setEmailDetailDoc(null)}
        />
      )}

      {/* Assign Action Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Assign Action</h2>
              <p className="text-sm text-gray-600 mb-4">
                Document: <span className="font-medium">{assignDoc?.name}</span>
              </p>
              <form onSubmit={(e) => { e.preventDefault();
                if (!assignForm.assigned_to) { showError('Please select a user to assign to'); return; }

                // Build the action data
                const actionData = {
                  document_id: assignDoc.id,
                  assigned_to: assignForm.assigned_to,
                  action_type: assignForm.action_type,
                  priority: assignForm.priority,
                  due_date: assignForm.due_date || null,
                  notes: assignForm.notes || null
                };

                console.log('Assign Doc:', assignDoc);
                console.log('Is SharePoint?', assignDoc.source === 'sharepoint');

                // Add SharePoint metadata if this is a SharePoint document
                if (assignDoc.source === 'sharepoint') {
                  console.log('Adding SharePoint metadata');
                  actionData.sharepoint_metadata = {
                    connectionId: assignDoc.connectionId,
                    driveId: assignDoc.driveId,
                    siteId: assignDoc.siteId,
                    fileName: assignDoc.name,
                    webUrl: assignDoc.webUrl || assignDoc.web_url,
                    tenantName: assignDoc.tenantName,
                    driveName: assignDoc.parentReference?.driveType || 'Documents',
                    mimeType: assignDoc.mimeType || assignDoc.file?.mimeType,
                    size: assignDoc.size
                  };
                }

                console.log('Action Data:', actionData);
                assignActionMutation.mutate(actionData);
              }}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Assign To <span className="text-red-500">*</span></label>
                    <select value={assignForm.assigned_to} onChange={(e) => setAssignForm({ ...assignForm, assigned_to: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                      <option value="">Select a user...</option>
                      {usersData?.data?.users?.map((user) => (
                        <option key={user.id} value={user.id}>{user.full_name} ({user.email})</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Action Type <span className="text-red-500">*</span></label>
                    <select value={assignForm.action_type} onChange={(e) => setAssignForm({ ...assignForm, action_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                      <option value="approval">Approval</option>
                      <option value="review">Review</option>
                      <option value="signature">Signature</option>
                      <option value="acknowledgment">Acknowledgment</option>
                      <option value="feedback">Feedback</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Priority <span className="text-red-500">*</span></label>
                    <select value={assignForm.priority} onChange={(e) => setAssignForm({ ...assignForm, priority: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent" required>
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                    <input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                    <textarea value={assignForm.notes} onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" rows={3} />
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button type="button" onClick={() => { setShowAssignModal(false); setAssignDoc(null); }} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
                    <button 
                      type="submit" 
                      disabled={assignActionMutation.isPending}
                      className="btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {assignActionMutation.isPending ? (
                        <>
                          <FiLoader className="animate-spin" />
                          Processing...
                        </>
                      ) : (
                        'Assign Action'
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Download Choice Modal */}
      {showSharePointDownloadModal && sharePointDocForDownload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Download Options</h2>
              <p className="text-sm text-gray-600 mb-6">
                How would you like to access <span className="font-medium">{sharePointDocForDownload.name}</span>?
              </p>

              <div className="space-y-4">
                {/* Option 1: View in SharePoint */}
                <button
                  onClick={() => {
                    if (sharePointDocForDownload.web_url) {
                      window.open(sharePointDocForDownload.web_url, '_blank');
                    }
                    setShowSharePointDownloadModal(false);
                    setSharePointDocForDownload(null);
                    setSelectedFolderId('');
                  }}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FiEye className="w-5 h-5" />
                  View in SharePoint
                </button>

                {/* Folder Selection for Upload */}
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Folder for Upload <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedFolderId}
                    onChange={(e) => setSelectedFolderId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                  >
                    <option value="">Select a folder...</option>
                    {foldersData?.data?.folders
                      ?.sort((a, b) => (a.folder_path || '').localeCompare(b.folder_path || ''))
                      .map((folder) => {
                        // Calculate indentation based on folder_path depth
                        const depth = (folder.folder_path?.split('/').filter(Boolean).length || 1) - 1;
                        const prefix = '  '.repeat(depth) + (depth > 0 ? '└─ ' : '');
                        return (
                          <option key={folder.id} value={folder.id}>
                            {prefix}{folder.name}
                          </option>
                        );
                      })}
                  </select>
                </div>

                {/* Option 2: Upload to Doxolve */}
                <button
                  onClick={async () => {
                    if (!selectedFolderId) {
                      showError('Please select a folder first');
                      return;
                    }
                    setShowSharePointDownloadModal(false);
                    showInfo('Downloading and processing SharePoint file...', 5000);
                    try {
                      const response = await sharepointAPI.processFile({
                        connectionId: sharePointDocForDownload.connectionId,
                        driveId: sharePointDocForDownload.driveId,
                        itemId: sharePointDocForDownload.id,
                        fileName: sharePointDocForDownload.name,
                        siteId: sharePointDocForDownload.siteId,
                        siteName: sharePointDocForDownload.tenantName,
                        driveName: sharePointDocForDownload.parentReference?.driveType || 'Documents',
                        folderId: selectedFolderId
                      });

                      showSuccess('SharePoint file uploaded to Doxolve successfully!');
                      queryClient.invalidateQueries(['search']);

                      // Navigate to the new document
                      if (response.data.documentId) {
                        navigate(`/documents/${response.data.documentId}`);
                      }
                    } catch (error) {
                      console.error('Process error:', error);
                      showError(error.response?.data?.error || 'Failed to upload to Doxolve');
                    } finally {
                      setSharePointDocForDownload(null);
                      setSelectedFolderId('');
                    }
                  }}
                  disabled={!selectedFolderId}
                  className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiDownload className="w-5 h-5" />
                  Upload to Doxolve
                </button>

                {/* Cancel Button */}
                <button
                  onClick={() => {
                    setShowSharePointDownloadModal(false);
                    setSharePointDocForDownload(null);
                    setSelectedFolderId('');
                  }}
                  className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Search;